import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';

/** This many program errors within the window mean the app is in a state nobody can vouch for. */
const MAX_PROGRAM_ERRORS = 5;
const WINDOW_MS = 60000;

/**
 * What happens to sensitive data when an exception occurs (BSI TR-03161-2 O.Source_5):
 *
 *  - Every unhandled exception drops the short-lived sensitive data that only exists for one operation
 *    (for example the health-report data of an export that failed half way). Services register such data with
 *    `registerWiper()`.
 *  - When program errors keep coming (5 within a minute), the app is not trusted any more: access is cancelled
 *    and the data deleted - the access token and the user data are dropped from memory, local storage is cleared
 *    and the identity provider session is ended, which also reloads the page and so frees everything else.
 *
 * A failed HTTP call is not a program error and does not count: it is the backend's answer, handled elsewhere.
 */
@Injectable({ providedIn: 'root' })
export class FailClosedService {
    // looked up on demand: this service is created early, while the error handler is built
    private readonly _injector = inject(Injector);
    private readonly _wipers = new Set<() => void>();
    private _times: number[] = [];
    private _closed = false;

    /** Registers a function that deletes data which is only needed for the duration of one operation. */
    registerWiper(wiper: () => void): void {
        this._wipers.add(wiper);
    }

    /** True once access was cancelled. */
    get closed(): boolean {
        return this._closed;
    }

    /** To be called for every exception nothing else caught. Returns true when access was cancelled because of it. */
    onUnhandledError(error: unknown): boolean {
        if (this._closed) {
            return true;
        }
        this.wipeTransientData();

        if (error instanceof HttpErrorResponse) {
            return false;
        }
        const now = Date.now();
        this._times = this._times.filter((time) => now - time < WINDOW_MS);
        this._times.push(now);
        if (this._times.length >= MAX_PROGRAM_ERRORS) {
            this.failClosed();
            return true;
        }
        return false;
    }

    /** Deletes the data that is only needed for one operation. */
    wipeTransientData(): void {
        for (const wiper of this._wipers) {
            try {
                wiper();
            } catch {
                // wiping must never throw
            }
        }
    }

    /** Cancels access: deletes the transient data, the token and the user data, and ends the session. */
    failClosed(): void {
        if (this._closed) {
            return;
        }
        this._closed = true;
        this.wipeTransientData();
        try {
            // clears the token and local storage and ends the identity provider session (the page is left)
            this._injector.get(AuthService).signOut().subscribe();
        } catch {
            // go on with the rest
        }
        try {
            this._injector.get(UserService).clear();
        } catch {
            // nothing more can be done
        }
    }
}
